import { Job } from "../models/Job";
import { getAuthHeaders } from "../utils/authHeaders";
import { API_URL } from "../config/api";

// Check if backend is available
export const checkBackendStatus = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/jobs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
    });
    return response.ok;
  } catch (error) {
    return false;
  }
};

// Pagination response interface
export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalJobs: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface JobsResponse {
  jobs: Job[];
  pagination: PaginationInfo;
}

// Search/filter params interface
export interface JobSearchParams {
  page?: number;
  limit?: number;
  keywords?: string;
  location?: string;
  jobCategory?: string[];
  employmentType?: string[];
  experience?: string[];
  careerLevel?: string[];
  salaryMin?: number;
  salaryMax?: number;
}

// Fetch jobs list with pagination and filtering
export const fetchJobsWithPagination = async (params: JobSearchParams = {}): Promise<JobsResponse> => {
  try {

    // Build query string
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.keywords) queryParams.append('keywords', params.keywords);
    if (params.location) queryParams.append('location', params.location);
    if (params.jobCategory?.length) queryParams.append('jobCategory', params.jobCategory.join(','));
    if (params.employmentType?.length) queryParams.append('employmentType', params.employmentType.join(','));
    if (params.experience?.length) queryParams.append('experience', params.experience.join(','));
    if (params.careerLevel?.length) queryParams.append('careerLevel', params.careerLevel.join(','));
    if (params.salaryMin) queryParams.append('salaryMin', params.salaryMin.toString());
    if (params.salaryMax) queryParams.append('salaryMax', params.salaryMax.toString());

    const url = `${API_URL}/jobs?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Jobs fetched from backend:', data.jobs?.length || 0, 'jobs');

      // Map backend _id to frontend id if needed
      const mappedJobs = (data.jobs || []).map((job: any) => ({
        ...job,
        id: job._id || job.id
      }));

      return {
        jobs: mappedJobs,
        pagination: data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalJobs: mappedJobs.length,
          limit: 10,
          hasNextPage: false,
          hasPrevPage: false,
        },
      };
    }

    // A failed request is not an empty register. Returning [] here made every
    // network fault, server error and unreadable browser cache render as
    // "No positions match the current filters", which the caller cannot tell
    // apart from a genuine empty result. Throw so callers can show a fault.
    throw new Error(`Jobs request failed: ${response.status} ${response.statusText}`);
  } catch (error) {
    console.error('❌ Backend error:', error);
    throw error;
  }
};

// Legacy fetch jobs (for backward compatibility)
export const fetchJobs = async (): Promise<Job[]> => {
  const response = await fetchJobsWithPagination({ limit: 1000 });
  return response.jobs;
};

// Create a new job (employer only)
export const createJob = async (jobData: Partial<Job>): Promise<Job> => {
  try {

    const response = await fetch(`${API_URL}/jobs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(jobData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create job');
    }

    const data = await response.json();

    return {
      ...data,
      id: data._id || data.id,
    };
  } catch (error) {
    console.error('❌ Error creating job:', error);
    throw error;
  }
};

// Update an existing job (employer only)
export const updateJob = async (id: string, jobData: Partial<Job>): Promise<Job> => {
  try {

    const response = await fetch(`${API_URL}/jobs/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(jobData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update job');
    }

    const data = await response.json();

    return {
      ...data,
      id: data._id || data.id,
    };
  } catch (error) {
    console.error('❌ Error updating job:', error);
    throw error;
  }
};

// Delete a job (employer only)
export const deleteJob = async (id: string): Promise<void> => {
  try {

    const response = await fetch(`${API_URL}/jobs/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete job');
    }

    console.log('✅ Job deleted successfully');
  } catch (error) {
    console.error('❌ Error deleting job:', error);
    throw error;
  }
};

// Get jobs by employer
export const fetchJobsByEmployer = async (userId: string): Promise<Job[]> => {
  try {

    const response = await fetch(`${API_URL}/jobs/employer/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      console.error('❌ Failed to fetch employer jobs:', response.status);
      return [];
    }

    const data = await response.json();
    console.log('✅ Fetched', data.length, 'jobs for employer');

    return data.map((job: any) => ({
      ...job,
      id: job._id || job.id,
    }));
  } catch (error) {
    console.error('❌ Error fetching employer jobs:', error);
    return [];
  }
};

// Fetch similar jobs for a given job
export const fetchSimilarJobs = async (jobId: string): Promise<{ id: string; title: string; company: string; location: string }[]> => {
  try {
    const response = await fetch(`${API_URL}/jobs/${jobId}/similar`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
    });

    if (!response.ok) return [];

    const data = await response.json();
    return data.map((job: any) => ({
      id: job._id || job.id,
      title: job.title,
      company: job.company,
      location: job.location,
    }));
  } catch {
    return [];
  }
};

// Fetch single job details (full data)
export const fetchJobById = async (id: string): Promise<Job | null> => {
  try {
    
    const response = await fetch(`${API_URL}/jobs/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      credentials: 'include',
    });

    console.log('📡 Response status:', response.status);
    console.log('📡 Response ok:', response.ok);

    if (response.ok) {
      const data = await response.json();
      
      // Map backend _id to frontend id if needed
      const mappedJob = {
        ...data,
        id: data._id || data.id
      };
      
      return mappedJob;
    } else if (response.status === 404) {
      console.log('❌ Job not found in backend (404)');
      return null;
    } else {
      console.error('❌ Backend response not ok:', response.status, response.statusText);
      return null;
    }
  } catch (error) {
    console.error('❌ Backend error:', error);
    return null;
  }
};